/**
 * The copy a reading link shares (decision 58; part 38d log, LW5): the chapters the writer chose, in the book's order, as
 * the exports read them (part 21's table N: titled as the preview titles them, the open one as typed), the book's
 * metadata and theme, and every image their text or the cover names that the book holds as named, with nothing else of
 * the book. The images are read through `image`, so the copy is built from the store's bytes as they are (decision 37).
 */
import { encodeReadingCopy } from '@cockatrice/core';
import type { CopyImage, ImageReference } from '@cockatrice/core';
import { planBook } from '@cockatrice/export/epub';
import { serializeMetadata, serializeTheme } from '@cockatrice/layout';
import type { ExportedBook } from '../shell/book-export.ts';
import type { SharedChapter } from './links.ts';

/** A copy to share: its chapters (what is pinned) and its bytes (what is sealed). */
export interface ShareCopy {
  readonly chapters: readonly SharedChapter[];
  readonly copy: Uint8Array<ArrayBuffer>;
}

/**
 * LW5: the copy of `read`'s chapters among `chosen`, in the book's order, with the images `image` gives for the references
 * their text and the cover make (one it gives null for is left out: the reader shows it missing, as the preview does).
 */
export async function readingCopyOf(read: ExportedBook, chosen: ReadonlySet<string>, image: (reference: ImageReference) => Promise<CopyImage | null>): Promise<ShareCopy> {
  const chapters = read.chapters.filter((chapter) => chosen.has(chapter.id)).map(({ id, title, text }) => ({ id, title, text }));
  const plan = planBook({ ...read.book, chapters: chapters.map(({ title, text }) => ({ title, text })) });
  const images: CopyImage[] = [];
  for (const reference of plan.references) {
    const found = await image(reference);
    if (found !== null) images.push(found);
  }
  return { chapters, copy: encodeReadingCopy({ metadata: serializeMetadata(read.book.metadata), theme: serializeTheme(read.book.theme), chapters, images }) };
}
